import { action, computed, observable, reaction } from "mobx";
import Validatable from "../common/Validatable";
import {
    Helper as MSHelper,
    Adapter,
    AdapterType,
    ComposedSampleType,
    IndexType,
    ItemBoxBuilder,
    ItemBoxController,
    ItemType,
    LP1Box,
    LP2Box,
    LPEPOption,
    PlateType,
    Platform,
    Probe,
    TC1Box,
    TC2Box,
    TC3Box,
    UnitType,
    storageTempKeyType,
    ProbeConc,
    ItemVersion,
    PlatformList,
    TargetSpecies,
    TargetSpeciesMap,
    TargetSpeciesLabel,
    TargetSpeciesList,
    PanelSize,
    PanelSpecialOptions,
    PanelSizeLabel,
    PanelSizeList,
    PanelSizeLabelMap,
    PanelSpecialOptionsList,
    PanelSpecialOptionsLabel,
    PanelSizeValueMap,
    PanelSpecialOptionsValue,
    BTSeqPackagingType,
    BTSeqIndexType,
    BTSeq001ItemBox,
    BTSeq002ItemBox,
    BTSeq003ItemBox,
    BTSeqPackagingLabel,
    BTSeqIndexTypeLabel,
} from "@celemicsbisw/manufacture-item-structure";
import Helper from "../../../module/Helper";

export type TC1Version = "v1" | "v2";

export interface IManufactureItemOption {
    itemType?: ItemType;
    platform?: Platform;
    lpep?: LPEPOption;
    indexType?: IndexType;
    adapter?: Adapter;
    plate?: PlateType;
    tc1Enhancer?: boolean;
    tc2Enhancer?: boolean;
    version?: ItemVersion;
    panelSize?: PanelSize | null;
    specialOptions?: PanelSpecialOptions | null;
    mode?: Mode;
}

export type ManufactureOptionSelectionType = {
    label: string;
    value: any;
};

export type ProbeState = "solution" | "powder";

type Mode = "preset" | "options" | "btseq_options" | "btseq_preset" | null;

export class ManufactureItemOption extends Validatable {
    itemType?: ItemType;
    rxn: number | null;
    probeID: string | null;
    storageTemp?: storageTempKeyType;
    selectedUnit: UnitType;
    @observable platform?: Platform;
    @observable lpep?: LPEPOption;
    @observable indexType?: IndexType;
    @observable adapterType?: AdapterType;
    @observable plateType?: PlateType;
    @observable tc1Enhancer?: boolean;
    @observable tc2Enhancer?: boolean;

    // 260322 추가
    @observable tc2HybBufferPlus?: boolean;
    @observable tc1MethylEnhancer?: boolean;

    @observable composedSampleType: ComposedSampleType;
    @observable probeState: ProbeState;
    @observable version?: ItemVersion;
    @observable probeConc?: ProbeConc;
    @observable unit?: "rxn" | "ml" | "";
    @observable qty?: number;
    @observable numberPerQty?: number | null;
    @observable pooling: number | null;

    @observable targetSpecies?: TargetSpecies | null;
    @observable panelSize?: PanelSize | null;
    @observable specialOptions?: PanelSpecialOptions | null;

    // btseq
    @observable packagingType?: BTSeqPackagingType | null;
    @observable packagingType1?: BTSeqPackagingType | null;
    @observable packagingType2?: BTSeqPackagingType | null;
    @observable btseqIndexType?: BTSeqIndexType | null;

    @observable readonly: boolean;
    @observable hybBufferPlusEnabled: boolean;

    @observable selectedHybEnhancer?: 'hybEnhancer' | 'hybBufferPlus' | 'etc';

    mode: Mode;

    constructor(readonly: boolean = false, mode: Mode = "options") {
        super();
        this.itemType = null;
        this.platform = null;
        this.lpep = null;
        this.indexType = null;
        this.adapterType = null;
        this.plateType = "*";
        this.probeState = null;
        this.rxn = null;
        this.selectedUnit = "rxn";
        this.tc1Enhancer = false;
        this.tc2Enhancer = true;
        this.composedSampleType = null;
        this.version = null;
        this.readonly = readonly;
        this.mode = mode;
        this.unit = "rxn";
        if (mode === "preset") {
            this.pooling = 1;
        } else this.pooling = null;
        this.panelSize = null;
        this.specialOptions = "none";
        this.numberPerQty = null;
        this.packagingType = null;
        this.btseqIndexType = null;
        // this.hybBufferPlusEnabled = this.tc2Enhancer;
        this.tc1MethylEnhancer = this.tc1Enhancer;
        // this.tc2HybBufferPlus = true;
        // this.selectedHybEnhancer = this.tc2HybBufferPlus ? 'hybBufferPlus' : 'hybEnhancer';
        this.selectedHybEnhancer = 'hybBufferPlus';
        this.qty = null;
        if (!readonly) {
            this.initErrorsRecursive();
            this.tc2HybBufferPlus = true;
        }
    }

    getItemBoxLabelName() {
        switch (this.itemType) {
            case "lp1":
                return "1. Library preparation Box#1";
            case "lp2":
                return "2. Library preparation Box#2";
            case "tc1":
                return "3. Target capture solution Box#1";
            case "tc2":
                return "4. Target capture solution Box#2";
            case "tc3":
                return "5. Target capture solution Box#3";
            case "cDNA":
                return "cDNA Synthesis Kit";
            default:
                return null;
        }
    }

    /**
     * @TODO 아이템 클래스별로 validate 인터페이스 구현하게 해야함
     * @returns
     */
    validateSelf(): void {
        if (this.readonly) {
            return;
        }
        if (this.mode === "options") {
            if (this.itemType === "lp1") {
                if (!this.platform) {
                    this.setErrorTextByName("platform", Validatable.errorTexts.needValue);
                }
                if (!this.lpep) {
                    this.setErrorTextByName("lpep", Validatable.errorTexts.needValue);
                }
                if (this.version !== "v3") {
                    if (this.platform === "ionTorrent" && this.lpep === "ep") {
                        this.setErrorTextByName("lpep", "Ion torrent EP 는 Version 3 에서만 가능합니다.");
                    }
                }
                // if (!this.version) {
                //     this.setErrorTextByName('version', Validatable.errorTexts.needValue);
                // }
            }
            if (this.itemType === "lp2") {
                if (!this.platform) {
                    this.setErrorTextByName("platform", Validatable.errorTexts.needValue);
                }
                if (!this.adapterType) {
                    this.setErrorTextByName("adapterType", Validatable.errorTexts.needValue);
                }
                if (!this.indexType) {
                    this.setErrorTextByName("indexType", Validatable.errorTexts.needValue);
                }
                // if (!this.version) {
                //     this.setErrorTextByName('version', Validatable.errorTexts.needValue);
                // }
            }
            if (this.itemType === "tc1") {
                if (!this.platform) {
                    this.setErrorTextByName("platform", Validatable.errorTexts.needValue);
                }
                if (!this.indexType) {
                    this.setErrorTextByName("indexType", Validatable.errorTexts.needValue);
                }
                if (this.version === "v3") {
                    if (!this.targetSpecies) {
                        this.setErrorTextByName("targetSpecies", Validatable.errorTexts.needValue);
                    }
                    if (this.tc1Enhancer) {
                        if (this.targetSpecies !== "human") {
                            this.setErrorTextByName("targetSpecies", "Methyl enhancer 옵션 선택시 Human 만 선택 가능합니다.");
                        }
                    }
                }
                // if (!this.version) {
                //     this.setErrorTextByName('version', Validatable.errorTexts.needValue);
                // }
            }
            if (this.itemType === "tc2") {
                if (!this.platform) {
                    this.setErrorTextByName("platform", Validatable.errorTexts.needValue);
                }
                // if (!this.version) {
                //     this.setErrorTextByName('version', Validatable.errorTexts.needValue);
                // }
            }
            if (this.itemType === "tc3") {
                if (!this.platform) {
                    this.setErrorTextByName("platform", Validatable.errorTexts.needValue);
                }
                if (!this.probeState) {
                    this.setErrorTextByName("probeState", Validatable.errorTexts.needValue);
                }
                if (this.version === "v3") {
                    if (!this.panelSize) {
                        this.setErrorTextByName("panelSize", Validatable.errorTexts.needValue);
                    }
                    if (!this.specialOptions) {
                        this.setErrorTextByName("specialOptions", Validatable.errorTexts.needValue);
                    }

                    if (this.tc2HybBufferPlus) {
                        if (!this.tc2Enhancer) {
                            this.setErrorTextByName("tc2HybBufferPlus", "Hyb buffer plus 옵션은 Hyb enhancer가 선택되었을 경우만 선택 가능합니다.");
                        }
                    }
                }
            }
        } else if (this.mode === "preset") {
            if (this.version === "v3") {
                if (!this.panelSize) {
                    this.setErrorTextByName("panelSize", Validatable.errorTexts.needValue);
                }
                if (!this.specialOptions) {
                    this.setErrorTextByName("specialOptions", Validatable.errorTexts.needValue);
                }
            }
            if (!this.platform) {
                this.setErrorTextByName("platform", Validatable.errorTexts.needValue);
            }
            if (!this.lpep) {
                this.setErrorTextByName("lpep", Validatable.errorTexts.needValue);
            }
            if (!this.unit) {
                this.setErrorTextByName("unit", Validatable.errorTexts.needValue);
            }
            if (!this.qty) {
                this.setErrorTextByName("qty", Validatable.errorTexts.needValue);
            } else if (this.qty < 0) {
                this.setErrorTextByName("qty", Validatable.errorTexts.positive);
            }
            if (!this.numberPerQty) {
                this.setErrorTextByName("numberPerQty", Validatable.errorTexts.needValue);
            } else if (this.numberPerQty < 0) {
                this.setErrorTextByName("numberPerQty", Validatable.errorTexts.positive);
            }
            if (this.version !== "v3") {
                if (this.tc2HybBufferPlus) {
                    if (!this.tc2Enhancer) {
                        this.setErrorTextByName("tc2HybBufferPlus", "Hyb buffer plus 옵션은 Hyb enhancer가 선택되었을 경우만 선택 가능합니다.");
                    }
                }
                if (this.platform === "ionTorrent" && this.lpep === "ep") {
                    this.setErrorTextByName("lpep", "Ion torrent EP 는 Version 3 에서만 가능합니다.");
                }
            } else {
                if (!this.targetSpecies) {
                    this.setErrorTextByName("targetSpecies", Validatable.errorTexts.needValue);
                }
                if (this.tc1Enhancer) {
                    if (this.targetSpecies !== "human") {
                        this.setErrorTextByName("targetSpecies", "Methyl enhancer 옵션 선택시 Human 만 선택 가능합니다.");
                    }
                }
            }
            if (!this.adapterType) {
                this.setErrorTextByName("adapterType", Validatable.errorTexts.needValue);
            }
            if (!this.indexType) {
                this.setErrorTextByName("indexType", Validatable.errorTexts.needValue);
            }
            if (!this.probeState) {
                this.setErrorTextByName("probeState", Validatable.errorTexts.needValue);
            }
            // if (this.version === "v3") {
            //     // if (!this.probeConc) {
            //     //     this.setErrorTextByName("probeConc", Validatable.errorTexts.needValue);
            //     // }
            // }
            if (this.pooling === null || this.pooling === undefined) {
                this.setErrorTextByName("pooling", Validatable.errorTexts.needValue);
            }
            if (this.platform === 'mgi') {
                if (this.indexType) {
                    if (this.indexType === 'ad153') {
                        if (this.adapterType !== 'mgi2') {
                            this.setErrorTextByName('indexType', 'MGI Adapter, Index는 다음의 Pair 조합만 가능합니다. (MGI Single + MGI Adapter V2 / UDI + MGI Dual)')
                        }
                    }
                    if (this.indexType === 'udi') {
                        if (this.adapterType !== 'mgiDual') {
                            this.setErrorTextByName('indexType', 'MGI Adapter, Index는 다음의 Pair 조합만 가능합니다. (MGI Single + MGI Adapter V2 / UDI + MGI Dual)')
                        }
                    }
                }
                if (this.adapterType) {
                    if (this.adapterType === 'mgi2') {
                        if (this.indexType !== 'ad153') {
                            this.setErrorTextByName('adapterType', 'MGI Adapter, Index는 다음의 Pair 조합만 가능합니다. (MGI Single + MGI Adapter V2 / UDI + MGI Dual)');
                        }
                    }
                    if (this.adapterType === 'mgiDual') {
                        if (this.indexType !== 'udi') {
                            this.setErrorTextByName('adapterType', 'MGI Adapter, Index는 다음의 Pair 조합만 가능합니다. (MGI Single + MGI Adapter V2 / UDI + MGI Dual)');
                        }
                    }
                }
            }
        } else if (this.mode === "btseq_preset") {
            if (!this.packagingType1) {
                this.setErrorTextByName("packagingType1", Validatable.errorTexts.needValue);
            }
            if (!this.packagingType2) {
                this.setErrorTextByName("packagingType2", Validatable.errorTexts.needValue);
            }
            if (!this.btseqIndexType) {
                this.setErrorTextByName("btseqIndexType", Validatable.errorTexts.needValue);
            }
            if (!this.qty) {
                this.setErrorTextByName("qty", Validatable.errorTexts.needValue);
            } else if (this.qty < 0) {
                this.setErrorTextByName("qty", Validatable.errorTexts.positive);
            }
            if (!this.numberPerQty) {
                this.setErrorTextByName("numberPerQty", Validatable.errorTexts.needValue);
            } else if (this.numberPerQty < 0) {
                this.setErrorTextByName("numberPerQty", Validatable.errorTexts.positive);
            }
        }
    }

    @computed
    get indexTypeSelectionList() {
        if (!this.platform) return [];
        const comp = MSHelper.composeSampleType(this.lpep);
        return this.getIndexTypeList(this.platform, comp);
    }

    @computed
    get adapterSelectionList() {
        if (!this.platform) return [];
        const comp = MSHelper.composeSampleType(this.lpep);
        return this.getAdapterList(this.platform, comp);
    }

    @computed
    get lpepSelectionList() {
        if (!this.platform) return [];
        return this.getLPEPSelectionList(this.platform);
    }

    onAdapterChanged = reaction(
        () => ({
            platform: this.platform,
            adapterType: this.adapterType,
            version: this.version,
        }),
        ({ platform, adapterType, version }) => {
            if (platform === 'illumina') {
                if (adapterType === 'methyl') {
                    if (version === 'v3') {
                        this.tc1Enhancer = false;
                        this.tc1MethylEnhancer = true;
                        this.targetSpecies = 'human';
                        if (this.mode === 'preset') this.specialOptions = 'methylation';
                    }
                    else if (version === 'v1' || version === 'v2') {
                        this.tc1Enhancer = true;
                    }
                    this.tc2Enhancer = false;
                }
                else if (adapterType) {
                    this.tc1Enhancer = false;
                    this.tc1MethylEnhancer = false;
                    this.tc2Enhancer = true;
                }
            }
        },
        {
            fireImmediately: true,
        }
    )

    // setIndexTypeByLPEP = reaction(
    //     () => this.lpep,
    //     () => !this.readonly && (this.indexType = null),
    // )

    setIndexTypeByPlatform = reaction(
        () => this.platform,
        () => !this.readonly && (this.indexType = null),
    )

    // setAdapterByLPEP = reaction(
    //     () => this.lpep,
    //     () => !this.readonly && (this.adapterType = null),
    // )

    setAdapterByPlatform = reaction(
        () => this.platform,
        () => !this.readonly && (this.adapterType = null),
    )

    setSelectedLPEP = reaction(
        () => this.platform,
        () => !this.readonly && (this.lpep = null),
    )

    // onHybEnhancerChanged = reaction(
    //     () => this.tc2Enhancer,
    //     (value) => {
    //         // this.hybBufferPlusEnabled = value;
    //         // this.tc2HybBufferPlus = value;
    //         this.selectedHybEnhancer = this.tc2HybBufferPlus ? 'hybBufferPlus' : 'hybEnhancer';
    //         console.log(`tc2enhancer changed: ${this.selectedHybEnhancer}`);
    //     }
    // )

    // onHybBufferPlusChanged = reaction(
    //     () => this.tc2HybBufferPlus,
    //     (value) => {
    //         this.selectedHybEnhancer = value ? 'hybBufferPlus' : 'hybEnhancer';
    //         console.log(`tc2hybbufferplus changed: ${this.selectedHybEnhancer}`);
    //     }
    // )

    onHybOptionChanged = reaction(
        () => ({
            version: this.version,
            tc2Enhancer: this.tc2Enhancer,
            tc2HybBufferPlus: this.tc2HybBufferPlus,
        }),
        ({ version, tc2Enhancer, tc2HybBufferPlus }) => {
            if (version === 'v3') {
                this.selectedHybEnhancer = tc2HybBufferPlus
                    ? 'hybBufferPlus'
                    : tc2Enhancer
                        ? 'hybEnhancer'
                        : 'etc';
            }
            else if (version === 'v1' || version === 'v2') {
                this.selectedHybEnhancer = tc2Enhancer ?
                    'hybEnhancer' : null;
                this.tc2HybBufferPlus = false;
            }
            else {
                this.selectedHybEnhancer = tc2HybBufferPlus
                    ? 'hybBufferPlus'
                    : tc2Enhancer
                        ? 'hybEnhancer'
                        : 'etc';
                this.tc2Enhancer = tc2Enhancer;
                this.tc2HybBufferPlus = tc2HybBufferPlus;
            }
        },
        {
            fireImmediately: true,
        }
    );

    onSelectedHybOptionsChanged = reaction(
        () => this.selectedHybEnhancer,
        (value) => {
            if (value === 'hybBufferPlus') {
                this.tc2Enhancer = true;
                this.tc2HybBufferPlus = true;
            }
            else if (value === 'hybEnhancer') {
                this.tc2Enhancer = true;
                this.tc2HybBufferPlus = false;
            }
            else if (value === 'etc') {
                this.tc2Enhancer = false;
                this.tc2HybBufferPlus = false;
            }
        }
    )

    // onSelectedHybEnhancerChanged = reaction(
    //     () => this.selectedHybEnhancer,
    //     (value) => {
    //         if (value === 'hybEnhancer') {
    //             this.tc2Enhancer = true;
    //             this.tc2HybBufferPlus = false;
    //         }
    //         else if (value === 'hybBufferPlus') {
    //             this.tc2Enhancer = true;
    //             this.tc2HybBufferPlus = true;
    //         }
    //     }
    // )

    onTC1EnhancerChanged = reaction(
        () => this.tc1Enhancer,
        (value) => {
            if (value || (this.adapterType && this.adapterType !== 'methyl')) {
                this.tc1MethylEnhancer = value;
            }
        }
    )

    getTC2EnhancerSelection(): { label: string, value: string }[] {
        const list = [];
        if (this.version === 'v3') {
            list.push({
                label: 'Hyb Buffer Plus',
                value: 'hybBufferPlus'
            })
            list.push({
                label: 'Hyb Enhancer (개별 시약)',
                value: 'hybEnhancer'
            })
            list.push({
                label: 'etc. (기타 요청 사항)',
                value: 'etc'
            })
        }
        else {
            list.push({
                label: 'Hyb Enhancer',
                value: 'hybEnhancer'
            });
        }
        return list;
    }

    getAdapterList(platform: Platform, composedSampleType: ComposedSampleType): ManufactureOptionSelectionType[] {
        return MSHelper.extractElseSelectionInfosByKey(platform, composedSampleType, "adapter")
            ?? MSHelper.getFirstAdapterList(platform)
            ?? [];
    }

    getIndexTypeList(platform: Platform, composedSampleType: ComposedSampleType): ManufactureOptionSelectionType[] {
        return MSHelper.extractElseSelectionInfosByKey(platform, composedSampleType, "indexType")
            ?? MSHelper.getFirstIndexList(platform)
            ?? [];
    }

    getPlatformSelectionList(): { label: string; value: Platform }[] {
        const keys = MSHelper.getPlatformListByVersion(this.version);
        return keys.map((v) => {
            return {
                label: PlatformList[v],
                value: v,
            };
        });
    }

    getLPEPSelectionList(platform: Platform) {
        const sel = MSHelper.getLPEPSelectionList(platform);
        return sel;
    }

    getTargetSpeciesSelectionList(): { label: TargetSpeciesLabel; value: TargetSpecies }[] {
        const list = TargetSpeciesList.map((v) => {
            return { value: v, label: this.translateTargetSpecies(v) };
        });
        if (this.tc1Enhancer) return list.filter((v) => v.value === "human");
        return list;
    }

    getPanelSizeSelectionList(): { label: PanelSizeLabel; value: PanelSize }[] {
        const list = PanelSizeList.map((v) => {
            return { value: v, label: PanelSizeLabelMap[v] };
        });
        return list;
    }

    getPanelSpecialOptionsSelectionList(): { label: string; value: PanelSpecialOptions }[] {
        const list = PanelSpecialOptionsList.map((v) => {
            return { value: v, label: PanelSpecialOptionsLabel[v] };
        });
        return list;
    }

    @action.bound
    onChangeOptionValue = (key: keyof ManufactureItemOption, value: any) => {
        (this as any)[key] = value;
    };

    @action.bound
    reset = () => {
        this.itemType = null;
        this.platform = null;
        this.lpep = null;
        this.indexType = null;
        this.adapterType = null;
        this.plateType = "*";
        this.probeState = null;
        this.tc1Enhancer = false;
        this.tc2Enhancer = false;
        this.tc2HybBufferPlus = false;
        this.targetSpecies = null;
        this.panelSize = null;
        this.specialOptions = null;
        this.pooling = null;
        this.initErrorsRecursive();
    };

    private serializeThis() {
        return {
            // type: this.type,
            rxn: this.rxn,
            platform: this.platform,
            lpep: this.lpep,
            indexType: this.indexType,
            plateType: this.plateType,
            tc1Enhancer: this.tc1Enhancer,
            tc1MethylEnhancer: this.tc1MethylEnhancer,
            tc2Enhancer: this.tc2Enhancer,
            tc2HybBufferPlus: this.tc2HybBufferPlus,
            composedSampleType: this.composedSampleType,
            probeState: this.probeState,
            selectedUnit: this.selectedUnit,
            storageTemp: this.storageTemp,
            version: this.version,
            targetSpecies: this.targetSpecies,
            panelSize: this.panelSize,
            specialOptions: this.specialOptions,
            pooling: this.pooling,
            packagingType: this.packagingType,
            btseqIndexType: this.btseqIndexType,
        } as Partial<ManufactureItemOption>;
    }

    getItemBox() {
        const item = ItemBoxBuilder.build(this.itemType, { ...this.serializeThis() });
        if (item instanceof LP1Box) {
            item.lpep = this.lpep;
        }
        if (item instanceof LP2Box) {
            item.indexType = this.indexType;
            item.plateType = this.plateType;
            ItemBoxController.setAdpaterByJSON(item, this.adapterType);
        }
        if (item instanceof TC1Box) {
            item.enhancer = this.tc1Enhancer;
            item.tc1MethylEnhancer = this.tc1MethylEnhancer;
            item.indexType = this.indexType;
            item.targetSpecies = this.targetSpecies;
        }
        if (item instanceof TC2Box) {
            item.enhancer = this.tc2Enhancer;
            (item as TC2Box).tc2HybBufferPlus = this.tc2HybBufferPlus;
        }
        if (item instanceof TC3Box) {
            item.probe = new Probe({
                probeId: this.probeID,
                name: this.probeID,
                state: this.probeState,
            });
            if (this.version === "v3") {
                item.probeConc = this.probeConc;
                item.panelSize = this.panelSize;
                item.specialOptions = this.specialOptions;
                item.pooling = Number(this.pooling);
            }
        }
        if (item instanceof BTSeq001ItemBox) {
            item.packagingType = this.packagingType;
        }
        if (item instanceof BTSeq002ItemBox) {
            item.packagingType = this.packagingType;
        }
        if (item instanceof BTSeq003ItemBox) {
            item.indexType = this.btseqIndexType;
        }

        ItemBoxController.setReagentsByJSON(item);
        item.subReagentCode = item.getCalculatedSubReagentCode();
        return item;
    }

    translateProbeConc() {
        switch (this.probeConc) {
            case "low":
                return "Low";
            case "high":
                return "High";
            case "methyl":
                return "Methyl";
            case "etc":
                return "etc.";
            default:
                return null;
        }
    }

    getVersionString() {
        switch (this.version) {
            case "v1":
                return "V1";
            case "v2":
                return "V2";
            case "v3":
                return "V3";
            default:
                return "";
        }
    }

    getOptionsVersionString() {
        const str = this.getVersionString();
        if (str) {
            return `${str} / `;
        }
    }

    /**
     * @TODO 추후 Itembox 에 인터페이스 추가해야함
     * @returns
     */
    getOptionString() {
        const strs = [];
        switch (this.itemType) {
            case "lp1":
                this.version && strs.push(this.getVersionString());
                this.lpep && strs.push(this.lpep.toUpperCase());
                break;
            case "lp2":
                this.version && strs.push(this.getVersionString());
                this.translateIndex() && strs.push(this.translateIndex());
                this.translateAdapter() && strs.push(this.translateAdapter());
                break;
            case "tc1":
                this.version && strs.push(this.getVersionString());
                this.translateIndex() && strs.push(this.translateIndex());
                if (this.version === 'v3' && this.tc1MethylEnhancer) {
                    strs.push(this.translateSpecialOptions('methylation'));
                }
                else if (this.tc1Enhancer) {
                    strs.push("Methyl enhancer");
                }
                this.targetSpecies && strs.push(TargetSpeciesMap[this.targetSpecies]);
                break;
            case "tc2":
                this.version && strs.push(this.getVersionString());
                if (this.version !== 'v3') {
                    if (this.tc2Enhancer) {
                        strs.push('Hyb enhancer');
                    }
                }
                else {
                    if (this.selectedHybEnhancer === 'hybBufferPlus') {
                        strs.push('Hyb buffer plus');
                    }
                    else if (this.selectedHybEnhancer === 'hybEnhancer') {
                        strs.push('Hyb Enhancer (개별 시약)')
                    }
                    else if (this.selectedHybEnhancer === 'etc') {
                        strs.push('etc.(기타 요청 사항)');
                    }
                }
                break;
            case "tc3":
                this.version && strs.push(this.getVersionString());
                this.probeID && strs.push(this.probeID);
                this.probeState && strs.push(this.probeState);
                if (this.version === "v3") {
                    if (this.probeConc) strs.push(this.translateProbeConc());
                    if (this.panelSize) strs.push(this.translatePanelSizeOptionString(this.panelSize));
                    if (this.specialOptions) strs.push(this.translateSpecialOptions(this.specialOptions));
                }
                break;
            case "bt001":
                this.packagingType && strs.push(this.translateBTSeqPackagingType(this.packagingType));
                break;
            case "bt002":
                this.packagingType && strs.push(this.translateBTSeqPackagingType(this.packagingType));
                break;
            case "bt003":
                this.btseqIndexType && strs.push(this.translateBTSeqIndexType(this.btseqIndexType));
                break;
            default:
                return "";
        }
        return strs.join(" / ");
    }

    getPackgingOptionsString() {
        const options = this.getOptionString();
        const list = [];
        const spl = options.split(' / ');
        const version = spl.shift();
        if (version) {
            list.push(version);
        }
        list.push(Helper.fieldToString(this.platform) ?? undefined);
        list.push(...spl);
        return list.filter(v => v !== undefined).join(', ');
    }

    getCatNO() {
        if (this.version === "v1") {
            return null;
        }
        const item = this.getItemBox();
        if (item.isCatNoHasQuestion()) {
            return "";
        } else {
            return item.catNo;
        }
    }

    translatePlatform(platform: string = this.platform) {
        return PlatformList[platform as Platform] || platform;
    }

    translateIndex(indexType: string = this.indexType) {
        switch (indexType) {
            case "single":
                return "Single";
            case "ad153":
                return "MGI Index";
            case "dual":
                return "Dual";
            case "ionXpress":
                return "Barcode Adapter";
            case "udi":
                return "UDI";
            case "umi":
                return "UMI";
            case "mgiDual":
                return "MGI Dual Index";
            default:
                return indexType;
        }
    }

    translateAdapter(adapter: string = this.adapterType) {
        switch (adapter) {
            case "methyl":
                return "Methyl Adapter";
            case "mgi1":
                return "MGI Adapter V1";
            case "mgi2":
                return "MGI Adapter V2";
            case "normal":
                return "Short Adapter";
            case "p1":
                return "P1 Adapter";
            case "short":
                return "Short Adapter";
            case "umi":
                return "UMI Adapter";
            case "ushape":
                return "U-Shape Adapter";
            case "mgiDual":
                return "MGI Dual Adapter";
            default:
                return adapter;
        }
    }

    translateTargetSpecies(spec: TargetSpecies) {
        return TargetSpeciesMap[spec];
    }

    translatePanelSize(size: PanelSize) {
        return PanelSizeValueMap[size];
    }

    translatePanelSizeOptionString(size: PanelSize) {
        return PanelSizeLabelMap[size];
    }

    translateSpecialOptions(options: PanelSpecialOptions) {
        return PanelSpecialOptionsValue[options];
    }

    translateBTSeqPackagingType(type: BTSeqPackagingType) {
        return BTSeqPackagingLabel[type];
    }

    translateBTSeqIndexType(type: BTSeqIndexType) {
        return BTSeqIndexTypeLabel[type];
    }
}
